/**
 * Component: Visual Orbit v2.0 (Product Modal)
 * Description: Implements "Floating" Modal with Glassmorphism, LERP Zoom, and Pinch-to-Zoom.
 * Protocol: RFC 9842 Optimized
 */

(function () {
    'use strict';

    class VisualOrbit {
        constructor() {
            this.active = false;
            this.currentScale = 1;
            this.currentTranslateX = 0;
            this.currentTranslateY = 0;
            this.pointers = [];
            this.initialPinchDistance = 0;

            // LERP Configuration (Smoothness Factor)
            this.lerpFactor = 0.1;
            this.targetScale = 1;
            this.targetTranslateX = 0;
            this.targetTranslateY = 0;

            this.init();
        }

        init() {
            this.bindTriggers();
            this.injectModal();
            this.rafId = null;
        }

        bindTriggers() {
            // Locate triggers - semantic buttons
            const triggers = document.querySelectorAll('.visual-orbit-trigger');
            triggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault(); // Gravity Anchor: Prevents scroll/redirect
                    const imageSrc = trigger.dataset.orbitSrc;
                    const imageAlt = trigger.dataset.orbitAlt;
                    this.openOrbit(imageSrc, imageAlt);
                });
            });
        }

        injectModal() {
            if (document.getElementById('visual-orbit-modal')) return;

            const modalHTML = `
                <div id="visual-orbit-modal" class="visual-orbit-modal" role="dialog" aria-modal="true" aria-hidden="true" tabindex="-1">
                    <div class="orbit-backdrop"></div>
                    <div class="orbit-container">
                        <button class="orbit-close" aria-label="Close Visual Orbit">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                        <div class="orbit-viewport">
                            <img id="orbit-target" src="" alt="" draggable="false" />
                        </div>
                    </div>
                </div>
            `;
            document.body.insertAdjacentHTML('beforeend', modalHTML);

            this.modal = document.getElementById('visual-orbit-modal');
            this.img = document.getElementById('orbit-target');
            this.closeBtn = this.modal.querySelector('.orbit-close');
            this.backdrop = this.modal.querySelector('.orbit-backdrop');

            this.bindModalEvents();
        }

        bindModalEvents() {
            this.closeBtn.addEventListener('click', () => this.closeOrbit());
            this.backdrop.addEventListener('click', () => this.closeOrbit());

            // Keyboard Traps
            this.modal.addEventListener('keydown', (e) => this.handleKeydown(e));

            // Zoom Interactions
            this.viewport = this.modal.querySelector('.orbit-viewport');

            // Pointer Events for Pinch-to-Zoom & Pan
            this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
            this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
            this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
            this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));
            this.viewport.addEventListener('pointerleave', this.onPointerUp.bind(this));

            // Wheel Zoom (Desktop)
            this.viewport.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        }

        openOrbit(src, alt) {
            this.active = true;
            this.img.src = src;
            this.img.alt = alt || 'Product Detail';
            this.modal.setAttribute('aria-hidden', 'false');
            this.modal.classList.add('is-active');

            // Focus Trap
            this.previousActiveElement = document.activeElement;
            this.modal.focus();

            // Reset Transform
            this.resetTransform();

            // Start Animation Loop
            this.animate();
        }

        closeOrbit() {
            this.active = false;
            this.modal.setAttribute('aria-hidden', 'true');
            this.modal.classList.remove('is-active');

            if (this.previousActiveElement) {
                this.previousActiveElement.focus();
            }

            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
            }
        }

        handleKeydown(e) {
            if (e.key === 'Escape') this.closeOrbit();
            if (e.key === 'Tab') {
                // Simple Focus Keep-Alive (Only close button is actionable currently)
                e.preventDefault();
                this.closeBtn.focus();
            }
        }

        // --- Physics Engine (Zero Mass) ---

        onPointerDown(e) {
            e.preventDefault();
            this.pointers.push(e);
            this.isDragging = false; // Reset drag state
            if (this.pointers.length === 2) {
                this.initialPinchDistance = this.getDistance(this.pointers[0], this.pointers[1]);
            }
        }

        onPointerMove(e) {
            e.preventDefault(); // Prevent browser panning

            // Update pointer record
            const index = this.pointers.findIndex(p => p.pointerId === e.pointerId);
            if (index !== -1) {
                this.pointers[index] = e;
            }

            // Pinch Logic
            if (this.pointers.length === 2) {
                const currentDistance = this.getDistance(this.pointers[0], this.pointers[1]);
                const diff = currentDistance - this.initialPinchDistance;

                // Sensitive Pinch Factor
                this.targetScale = Math.max(1, Math.min(5, this.currentScale + diff * 0.01));
                this.initialPinchDistance = currentDistance; // Reset for continuous delta
            }
            // Pan Logic (Single Finger / Mouse Drag)
            else if (this.pointers.length === 1 && this.targetScale > 1) {
                this.targetTranslateX += e.movementX;
                this.targetTranslateY += e.movementY;
                if (Math.abs(e.movementX) > 1 || Math.abs(e.movementY) > 1) {
                    this.isDragging = true;
                }
            }
        }

        onPointerUp(e) {
            const index = this.pointers.findIndex(p => p.pointerId === e.pointerId);
            if (index !== -1) {
                this.pointers.splice(index, 1);
            }
            if (this.pointers.length < 2) {
                this.initialPinchDistance = 0;
            }

            // Vacuum Click (Tap on Void)
            if (!this.isDragging && e.target === this.viewport && this.pointers.length === 0) {
                this.closeOrbit();
            }
        }

        onWheel(e) {
            e.preventDefault();
            const delta = -Math.sign(e.deltaY) * 0.2;
            this.targetScale = Math.max(1, Math.min(5, this.targetScale + delta));
        }

        getDistance(p1, p2) {
            return Math.sqrt(Math.pow(p2.clientX - p1.clientX, 2) + Math.pow(p2.clientY - p1.clientY, 2));
        }

        resetTransform() {
            this.currentScale = 1;
            this.currentTranslateX = 0;
            this.currentTranslateY = 0;
            this.targetScale = 1;
            this.targetTranslateX = 0;
            this.targetTranslateY = 0;
            this.render();
        }

        // LERP Animation Loop
        animate() {
            if (!this.active) return;

            // LERP for Scale
            this.currentScale += (this.targetScale - this.currentScale) * this.lerpFactor;

            // LERP for Position
            this.currentTranslateX += (this.targetTranslateX - this.currentTranslateX) * this.lerpFactor;
            this.currentTranslateY += (this.targetTranslateY - this.currentTranslateY) * this.lerpFactor;

            // Boundaries (Elastic Gravity)
            if (this.targetScale <= 1.01) {
                this.targetTranslateX = 0;
                this.targetTranslateY = 0;
            }

            this.render();
            this.rafId = requestAnimationFrame(this.animate.bind(this));
        }

        render() {
            this.img.style.transform = `translate(${this.currentTranslateX}px, ${this.currentTranslateY}px) scale(${this.currentScale})`;
        }
    }

    // Initialize on DOM Ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new VisualOrbit());
    } else {
        new VisualOrbit();
    }

})();
